import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk,
  createSelector,
  createAction,
} from '@reduxjs/toolkit';
import axios from 'axios';
import type { Project } from 'types/store/projectsList';
import { SLICE_STATUS } from 'utils/constants';
import { Status } from 'utils/customTypes';
import {
  CONSOLIDATED_PROJECTS_FIELD_PARAM,
  CONSOLIDATED_PROJECTS_INCLUDE_PARAM,
  QuickFilter,
} from 'utils/constants/projectsList';
import { ProjectPermission } from 'utils/types/projectsList';
import { fetchProjectsList } from 'api/projects';
import { RootState } from 'state/store';
import { selectFilterParams } from './filters/filtersSlice';
import { deserialize, normalize, buildJsonApiUrl } from './helpers';
import { updateOwners } from './include/ownersSlice';
import { updateLdTeams } from './include/ldTeamsSlice';
import { updateBusinessTeams } from './include/businessTeamsSlice';
import { updateProcesses } from './include/processesSlice';
import { updateStages } from './include/stagesSlice';
import { updatePrograms } from './include/programsSlice';

interface ProjectsListState {
  status?: Status;
  links?: {
    next?: string;
    self?: string;
  };
  totalCount?: number;
  isFetchingMore?: boolean;
  displaySidePanelFilters?: boolean;
  displayDuplicateProjectModal?: boolean;
  displayArchiveProjectModal?: boolean;
  permissions?: Record<string, ProjectPermission>;
}

export interface FetchProjectsThunkParams {
  includeTotalCount?: boolean;
  quickFilters?: QuickFilter[];
  isConsolidatedView?: boolean;
}

const initialState: ProjectsListState = {};

const projectsAdapter = createEntityAdapter<Project>();

export const fetchProjects = createAsyncThunk(
  'projectsList/fetchProjects',
  async (
    {
      includeTotalCount,
      isConsolidatedView = false,
    }: FetchProjectsThunkParams = {},
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = getState() as RootState;
      const projectsListState = state.projectsList.table;
      const { isFetchingMore, links } = projectsListState;
      const filterParams = selectFilterParams(state);
      let url = '';
      if (isFetchingMore && links?.next) {
        url = links.next;
      } else {
        const params = {
          ...filterParams,
          field: isConsolidatedView
            ? CONSOLIDATED_PROJECTS_FIELD_PARAM
            : undefined,
          include: isConsolidatedView
            ? CONSOLIDATED_PROJECTS_INCLUDE_PARAM
            : undefined,
        };
        url = buildJsonApiUrl(params, isConsolidatedView);
      }
      const projects = await fetchProjectsList({ url, includeTotalCount });
      const deserializedProjects = await deserialize(projects);
      const normalizedProjects = normalize(deserializedProjects);
      dispatch(
        updateOwners({
          owners: normalizedProjects.projectOwners,
          shouldReplace: isFetchingMore ? false : true,
        })
      );
      dispatch(
        updateLdTeams({
          ldTeams: normalizedProjects.ldTeams,
          shouldReplace: isFetchingMore ? false : true,
        })
      );
      dispatch(
        updateBusinessTeams({
          businessTeams: normalizedProjects.businessTeams,
          shouldReplace: isFetchingMore ? false : true,
        })
      );
      dispatch(
        updateProcesses({
          processes: normalizedProjects.processes,
          shouldReplace: isFetchingMore ? false : true,
        })
      );
      dispatch(
        updateStages({
          stages: normalizedProjects.stages,
          shouldReplace: isFetchingMore ? false : true,
        })
      );
      dispatch(
        updatePrograms({
          programs: normalizedProjects.programs,
          shouldReplace: isFetchingMore ? false : true,
        })
      );
      return {
        ...normalizedProjects,
        links: projects.links,
        meta: projects.meta,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const updateProjectInList = createAction<{
  projectId: string;
  updatedProject: Partial<Project>;
}>('projectsList/updateProjectInList');

const projectsListSlice = createSlice({
  name: 'projectsList',
  initialState: {
    ...initialState,
    projects: projectsAdapter.getInitialState(),
  },
  reducers: {
    setFetchingMore: (state, action) => {
      state.isFetchingMore = action.payload;
    },
    toggleSidePanelFilters: (state) => {
      state.displaySidePanelFilters = !state.displaySidePanelFilters;
    },
    resetState: (state) => {
      state.status = undefined;
      state.links = undefined;
      state.totalCount = undefined;
      state.isFetchingMore = undefined;
      state.displaySidePanelFilters = undefined;
      state.displayDuplicateProjectModal = undefined;
      state.displayArchiveProjectModal = undefined;
      projectsAdapter.removeAll(state.projects);
    },
    toggleDuplicateProjectModal: (state) => {
      state.displayDuplicateProjectModal = !state.displayDuplicateProjectModal;
    },
    toggleArchiveProjectModal: (state) => {
      state.displayArchiveProjectModal = !state.displayArchiveProjectModal;
    },
  },
  extraReducers: (builder) => {
    return builder
      .addCase(fetchProjects.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        const { projects, links, meta, permissions } = action.payload;
        const isFetchingMore = state.isFetchingMore;
        if (isFetchingMore) {
          projectsAdapter.upsertMany(state.projects, projects);
        } else {
          projectsAdapter.setAll(state.projects, projects);
        }
        if (permissions) {
          state.permissions = {
            ...state.permissions,
            ...permissions,
          };
        }
        state.isFetchingMore = false;
        state.links = links;
        if (meta && 'totalCount' in meta) {
          state.totalCount = meta.totalCount;
        }
        state.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(fetchProjects.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(updateProjectInList, (state, action) => {
        const { projectId, updatedProject } = action.payload;
        projectsAdapter.updateOne(state.projects, {
          id: projectId,
          changes: updatedProject,
        });
      });
  },
});

const getProjectsState = (state: RootState) => state.projectsList.table;

export const {
  selectAll: selectAllProjects,
  selectIds: selectProjectsIds,
  selectById: selectProjectById,
} = projectsAdapter.getSelectors<RootState>(
  (state) => getProjectsState(state).projects
);

export const selectProjectsListStatus = (state: RootState) =>
  getProjectsState(state).status;

export const selectCanFetchMoreProjects = (state: RootState) => {
  const links = getProjectsState(state).links;
  if (!links) {
    return false;
  }
  return !!links.next;
};

export const selectTotalProjectsCount = (state: RootState) =>
  getProjectsState(state).totalCount ?? 0;

export const selectLoadedProjectsCount = (state: RootState) =>
  getProjectsState(state).projects.ids.length;

export const selectIsLoading = (state: RootState) => {
  return (
    (!getProjectsState(state).status ||
      getProjectsState(state).status === SLICE_STATUS.LOADING) &&
    !getProjectsState(state).isFetchingMore
  );
};

export const selectIsSidePanelFiltersVisible = (state: RootState) =>
  getProjectsState(state).displaySidePanelFilters ?? false;

export const selectIsArchiveProjectModalVisible = (state: RootState) =>
  getProjectsState(state).displayArchiveProjectModal ?? false;

export const selectIsProjectsListEmpty = (state: RootState) =>
  selectProjectsIds(state).length === 0;

const selectProjectPermissions = (state: RootState) =>
  getProjectsState(state).permissions;

export const selectProjectPermissionsById = createSelector(
  [selectProjectPermissions, (_, projectId: string) => projectId as string],
  (permissions, projectId) =>
    permissions?.[projectId] ?? {
      canEdit: false,
      canDelete: false,
      canArchive: false,
      canView: false,
    }
);

export const selectIsProjectArchived = createSelector(
  [
    (state: RootState, projectId: string) =>
      selectProjectById(state, projectId),
  ],
  (project) => project?.isArchived ?? false
);

export const {
  setFetchingMore,
  toggleSidePanelFilters,
  resetState,
  toggleDuplicateProjectModal,
  toggleArchiveProjectModal,
} = projectsListSlice.actions;

export const selectShouldShowDuplicateProjectModal = createSelector(
  [getProjectsState],
  (state) => {
    return state.displayDuplicateProjectModal ?? false;
  }
);

export default projectsListSlice.reducer;
